import * as _ from 'lodash-es';
import { EnableOption } from '../admin/integration/IntegrationSelectSetting';
import type { BrandAndProductProps } from '../components/ExternalBrandLogo';
import directoryIcon from '../icons/non-prefilled/16x16/directory.svg';
import githubRepoIcon from '../icons/non-prefilled/16x16/githubRepo.svg';
import asanaWorkspaceIcon from '../icons/prefilled/brands/asanaWorkspace.svg';
import slackIcon from '../icons/prefilled/brands/slack.svg';
import zendeskIcon from '../icons/prefilled/brands/zendesk.svg';

export enum MentionMode {
  Mention = 'MENTION',
  Embed = 'EMBED',
}

export enum SearchPreviewMode {
  Batched = 'batched',
  Single = 'single',
}

interface MentionInfo {
  mentionModes?: MentionMode[];
  mentionName?: string;
  mentionDisplayName?: string;
  parentDisabled?: boolean;
  parentIcon?: string;
  parentName?: string;
  searchEnabledOptions?: EnableOption[];
}

interface RepoSyncInfo {
  topicMapping?: boolean;
}

export interface IntegrationPlatformInfo {
  description: string;
  icon: BrandAndProductProps;
  isPremium?: boolean;
  mention?: MentionInfo;
  name: string;
  platform: string;
  repoSync?: RepoSyncInfo;
  teamSearchPlatform?: string;
}

export interface ExternalSearchPlatformInfo extends IntegrationPlatformInfo {
  encourageInstallation?: boolean;
  searchPreview?: SearchPreviewMode;
}

type IntegrationPlatformBase = Partial<IntegrationPlatformInfo>;
type ExternalSearchPlatformBase = Partial<ExternalSearchPlatformInfo>;

type BaseMap = Record<string, IntegrationPlatformBase>;
type ExternalSearchBaseMap = Record<string, ExternalSearchPlatformBase>;

const base: BaseMap = {
  asana: {
    description: 'Preview Asana tasks in posts.',
    mention: {
      parentName: 'workspace',
      parentIcon: asanaWorkspaceIcon,
      mentionName: 'task',
      mentionModes: [MentionMode.Mention],
      searchEnabledOptions: [
        EnableOption.all,
        EnableOption.custom,
        EnableOption.disabled,
      ],
    },
  },
  bamboohr: {
    name: 'BambooHR',
    description: 'Autofill user profile attributes.',
  },
  clickup: {
    description: 'Preview ClickUp tasks in posts.',
    name: 'ClickUp',
    mention: {
      parentName: 'team',
      mentionName: 'task',
      mentionModes: [MentionMode.Mention],
    },
  },
  shortcut: {
    description: 'Preview Shortcut stories and epics in posts.',
    mention: {
      mentionName: 'story',
      parentName: 'project',
      mentionModes: [MentionMode.Mention],
    },
  },
  dropbox: {
    description: 'Enable Dropbox search for your entire team.',
    mention: {
      parentIcon: directoryIcon,
      parentName: 'directory',
      searchEnabledOptions: [
        EnableOption.public,
        EnableOption.custom,
        EnableOption.disabled,
      ],
    },
  },
  freshdesk: {
    description: 'Preview Freshdesk tickets in posts.',
    mention: {
      mentionName: 'ticket',
      parentName: 'group',
      mentionModes: [MentionMode.Mention],
    },
  },
  github: {
    description:
      'Preview GitHub Issues in posts, and sync Markdown documents from GitHub repos.',
    name: 'GitHub',
    repoSync: { topicMapping: true },
    mention: {
      mentionName: 'issue',
      parentName: 'repository',
      parentIcon: githubRepoIcon,
      mentionModes: [MentionMode.Mention],
      searchEnabledOptions: [
        EnableOption.all,
        EnableOption.custom,
        EnableOption.disabled,
      ],
    },
  },
  gitlab: {
    description:
      'Preview GitLab Issues in posts, and sync Markdown documents from GitLab projects.',
    name: 'GitLab',
    repoSync: { topicMapping: false },
    mention: {
      mentionName: 'issue',
      parentName: 'project',
      mentionModes: [MentionMode.Mention],
    },
  },
  google: {
    description: 'Single Sign-On with Google Workspace.',
    teamSearchPlatform: 'google-drive',
    mention: {
      parentIcon: directoryIcon,
      parentName: 'drive',
      mentionName: 'drive_doc',
      searchEnabledOptions: [EnableOption.all, EnableOption.disabled],
      mentionModes: [MentionMode.Mention, MentionMode.Embed],
    },
  },
  guru: {
    description: 'Preview Guru cards in posts.',
    mention: {
      mentionName: 'card',
      parentName: 'collection',
      parentIcon: directoryIcon,
      mentionModes: [MentionMode.Mention],
      searchEnabledOptions: [
        EnableOption.all,
        EnableOption.custom,
        EnableOption.disabled,
      ],
    },
  },
  height: {
    description: 'Preview Height Tasks in posts.',
    mention: {
      mentionName: 'task',
      parentName: 'list',
      parentIcon: directoryIcon,
      mentionModes: [MentionMode.Mention],
      searchEnabledOptions: [
        EnableOption.custom,
        EnableOption.disabled,
        EnableOption.public,
      ],
    },
  },
  jira: {
    description: 'Preview Jira issues in posts.',
    icon: { brand: 'atlassian', product: 'jira' },
    mention: {
      mentionName: 'issue',
      parentName: 'workspace',
      mentionModes: [MentionMode.Mention],
    },
  },
  linear: {
    description: 'Preview Linear issues in posts.',
    mention: {
      parentIcon: directoryIcon,
      mentionName: 'issue',
      parentName: 'project',
      mentionModes: [MentionMode.Mention],
      searchEnabledOptions: [
        EnableOption.all,
        EnableOption.custom,
        EnableOption.disabled,
      ],
    },
  },
  microsoft: {
    description:
      'Single Sign-On with Azure AD, search and preview posts with Teams.',
    isPremium: true,
    name: 'Microsoft',
  },
  monday: {
    description: 'Preview Monday boards and items in posts.',
    mention: {
      mentionName: 'pulse',
      mentionDisplayName: 'item',
      parentName: 'board',
      mentionModes: [MentionMode.Mention],
    },
  },
  okta: {
    description: 'Single Sign-On with Okta.',
    isPremium: true,
  },
  onelogin: {
    name: 'OneLogin',
    description: 'Single Sign-On with OneLogin.',
    isPremium: true,
  },
  quip: {
    description: 'Enable Quip search for your entire team.',
    mention: {
      mentionName: 'document',
      parentName: 'organization',
      parentIcon: directoryIcon,
      parentDisabled: true,
      mentionModes: [MentionMode.Mention, MentionMode.Embed],
      searchEnabledOptions: [EnableOption.all, EnableOption.disabled],
    },
  },
  slack: {
    description:
      'Single Sign-On with Slack, use Slack commands to create and search posts, and receive notifications in Slack.',
    mention: {
      parentIcon: slackIcon,
      parentName: 'channel',
      searchEnabledOptions: [
        EnableOption.custom,
        EnableOption.disabled,
        EnableOption.public,
      ],
    },
  },
  todoist: {
    description: 'Preview Todoist tasks in posts.',
    mention: {
      mentionName: 'task',
      parentName: 'project',
      mentionModes: [MentionMode.Mention],
    },
  },
  trello: {
    description: 'Preview Trello cards in posts.',
    mention: {
      mentionName: 'card',
      parentName: 'board',
      mentionModes: [MentionMode.Mention],
    },
  },
  wrike: {
    description: 'Preview Wrike tasks in posts.',
    mention: {
      mentionName: 'task',
      parentName: 'space',
      mentionModes: [MentionMode.Mention],
    },
  },
  zendesk: {
    description: 'Preview Zendesk Support tickets in posts.',
    mention: {
      mentionName: 'ticket',
      parentName: 'organization',
      parentIcon: zendeskIcon,
      mentionModes: [MentionMode.Mention, MentionMode.Embed],
      searchEnabledOptions: [
        EnableOption.all,
        EnableOption.custom,
        EnableOption.disabled,
      ],
    },
    isPremium: true,
  },
};
type UpcomingBaseMap = Record<
  string,
  {
    description: string;
    website: string;
  }
>;

const upcomingBase = {
  productboard: {
    description: 'Preview Productboard tasks in posts.',
    website: 'https://www.productboard.com/',
  },
  gmail: {
    description: 'Search Gmail emails within Slab.',
    website: 'http://gmail.com/',
  },
  outlook: {
    description: 'Search Outlook emails within Slab.',
    website: 'http://outlook.com/',
  },
  salesforce: {
    description: 'Mention Salesforce in posts.',
    website: 'https://www.salesforce.com/',
  },
  hubspot: {
    name: 'HubSpot',
    description: 'Search HubSpot CRM objects within Slab.',
    website: 'https://www.hubspot.com/',
  },
  helpscout: {
    name: 'Help Scout',
    description: 'Search Help Scout conversations within Slab.',
    website: 'https://www.helpscout.com/',
  },
  intercom: {
    description: 'Search Intercom conversations within Slab.',
    website: 'https://www.intercom.com/',
  },
} as const;

const enrichInfo = <
  T extends BaseMap | ExternalSearchBaseMap | UpcomingBaseMap,
>(
  integrations: T,
  additionalBase: BaseMap = {},
): Record<string, T[keyof T] & IntegrationPlatformInfo> =>
  Object.entries(integrations).reduce(
    (memo, [platform, integration]) => ({
      ...memo,
      [platform]: {
        icon: { brand: platform },
        name: _.capitalize(platform),
        ...(additionalBase[platform] || {}),
        platform,
        ...integration,
      },
    }),
    {},
  );

export const INSTALLABLE_PLATFORM_INFO_MAP = enrichInfo(base);

export const UPCOMING_PLATFORM_INFO_MAP =
  enrichInfo<Record<string, { description: string; website: string }>>(
    upcomingBase,
  );

export const SEARCH_PLATFORM_INFO_MAP: Record<
  string,
  ExternalSearchPlatformInfo
> = enrichInfo(
  {
    'google-drive': {
      name: 'Google Drive',
      icon: { brand: 'google', product: 'drive' },
      searchPreview: SearchPreviewMode.Batched,
    },
    asana: {},
    box: { searchPreview: SearchPreviewMode.Single },
    confluence: { icon: { brand: 'atlassian', product: 'confluence' } },
    dropbox: { searchPreview: SearchPreviewMode.Single },
    github: {},
    height: {},
    jira: {},
    linear: {},
    quip: { searchPreview: SearchPreviewMode.Single },
    slack: {},
    trello: {},
    zendesk: {},
    guru: { searchPreview: SearchPreviewMode.Single },
  },
  base,
);
